import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { List, StatusTip, Button } from 'tea-component';
import CardCommon from '@op/components/CardCommon';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import useLanguage from '@op/hooks/useLanguage';
import DiscussionItem from '../DiscussionItem';
import {
  IDiscussionItem,
  TSortBy,
  SORT_OPTIONS,
  activeKeysMap,
} from '../types';
import { getGameIdIsSame } from '@op/utils/url';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { useHotEventContext } from '../../../context';
import './index.scss';
import { useHotEventList } from '../../../hooks/useHotEventList';
import { CONTACT_NAME } from '@op/constants';
import IconGemini from '@/pages/opinion/assets/svg/icon-gemini.png';
import moment from 'moment';

interface DiscussionListProps {
  changeLoading: () => void;
  onSelectItem: (item: IDiscussionItem | null) => void;
  onDataStateChange?: (state: { isEmpty: boolean }) => void;
}

/**
 * 左侧：Discussions 列表（自己管理数据请求，支持滚动分页加载）
 *
 * 首次加载时若当前日期无数据（list 为空且接口返回 has_data_start_time），
 * 由 useHotEventList 内部自动触发日期回退跳转至有数据的日期范围。
 */
export default function DiscussionList({
  changeLoading,
  onSelectItem,
  onDataStateChange,
}: DiscussionListProps) {
  const { formatMessage } = useLanguage();
  const {
    gameInfo,
    filter,
    onFilterDateSync,
    sentimentValue,
    channelType,
    sortBy,
    onSortByChange,
    clusterCategoriesValue,
  } = useHotEventContext();
  const [selectItem, setSelectItem] = useState<IDiscussionItem | null>(null);

  // ─── 构建请求参数 ───
  const filterParams = useMemo(() => {
    const { isSameId } = getGameIdIsSame(gameInfo);
    if (!isSameId || filter === null) return undefined;
    return {
      ...gameInfo,
      sentiment_type: sentimentValue,
      sort_item: sortBy || 'total_comments',
      sort_model: 'desc',
      channel_type: channelType,
      filter_keyword_response: true,
      cluster_categories: clusterCategoriesValue === 'all' ? [] : [clusterCategoriesValue],
      ...(filter || {}),
    };
  }, [filter, sentimentValue, sortBy, gameInfo, channelType, clusterCategoriesValue]);

  // ─── 数据请求（支持分页加载，首次无数据时自动跳转至有数据日期） ───
  const {
    list,
    loading,
    loadingMore,
    hasMore,
    hasDataStartTime,
    hasDataEndTime,
    hintTag,
    loadMore,
    trendMap,
    timelineStartTime,
    timelineEndTime,
    trendLoading,
  } = useHotEventList(filterParams, {
    pageSize: 30,
    onDateFallback: onFilterDateSync,
  });

  // ─── 通知父组件数据状态（是否为空列表） ───
  useEffect(() => {
    onDataStateChange?.({ isEmpty: list.length === 0 && !loading });
  }, [list, loading, onDataStateChange]);

  // ─── 默认选中第一条 ───
  useEffect(() => {
    if (list.length > 0) {
      const exists =
        selectItem &&
        list.some((item) => item.cluster_id === selectItem.cluster_id);
      if (!exists) {
        const firstItem = list[0];
        setSelectItem(firstItem);
        onSelectItem(firstItem);
      }
    } else {
      setSelectItem(null);
      onSelectItem(null);
    }
  }, [list]);

  // ─── 点击列表项 ───
  const handleItemClick = useCallback(
    (item: IDiscussionItem) => {
      changeLoading();
      if (item) {
        buriedBtnPoint('HC_event', item.cluster_id);
      }
      setSelectItem(item);
      onSelectItem(item);
    },
    [changeLoading, onSelectItem],
  );

  // ─── 排序变更（服务端排序，重新请求） ───
  const handleSortChange = useCallback(
    (value: TSortBy) => {
      onSortByChange(value);
    },
    [onSortByChange],
  );

  // ─── 滚动到底部加载更多 ───
  const handleScrollBottom = useCallback(() => {
    if (!hasMore || loadingMore) return;
    loadMore(filterParams);
  }, [filterParams, hasMore, loadingMore, loadMore]);

  // ─── 跳转至有数据日期按钮 ───
  const getRefreshNewTimeElement = useCallback(() => {
    if (!hasDataEndTime) {
      return null;
    }
    return (
      <Button
        type="link"
        style={{ paddingLeft: 5 }}
        onClick={() => {
          onFilterDateSync({
            start_time: moment(hasDataStartTime).format('YYYY-MM-DD'),
            end_time: moment(hasDataEndTime).format('YYYY-MM-DD'),
          });
        }}
      >
        {formatMessage({ id: 'Click to view recent event.' })}
      </Button>
    );
  }, [hasDataStartTime, hasDataEndTime, onFilterDateSync, formatMessage]);

  // ─── 无数据时的自定义提示词 ───
  const emptyDescElement = useMemo(() => {
    let text: string = 'No data under current filter selections.';
    let isFlag = false;

    if (hintTag === '1') {
      text =
        'There is not enough data under the current filter to generate Hot Event.';
    } else if (hasDataEndTime) {
      text = 'No data under the current filter settings.';
      isFlag = true;
    } else {
      text =
        'This game is not configured with NLP tasks, feel free to contact {contact} if you need.';
    }

    return (
      <div style={{ marginTop: 5 }}>
        <p>
          {formatMessage({ id: text }, { contact: CONTACT_NAME })}
          {getRefreshNewTimeElement()}
          <br />
          <br />
          <div style={{ textAlign: 'center' }}>
            {isFlag &&
              formatMessage(
                {
                  id: 'If you have any question, feel free to contact {contact}.',
                },
                { contact: CONTACT_NAME },
              )}
          </div>
        </p>
      </div>
    );
  }, [hintTag, hasDataEndTime, getRefreshNewTimeElement, formatMessage]);

  const isEmpty = list.length === 0 && !loading;
  // 无数据时：有 endTime 才不显示默认 title（说明只是当前筛选无数据，附近有其他数据），
  // 没有 endTime 时显示 title（说明 NLP 未配置等系统性原因）
  const showEmptyTitle = isEmpty && !hasDataEndTime;
  // 始终用 emptyDescElement 替代默认描述
  const showEmptyDesc = false;

  return (
    <CardCommon
      title={
        <span className="dbo-discussion-title">
          <img src={IconGemini} alt="" className="dbo-discussion-title-icon" />
          <span>{formatMessage({ id: 'Discussions' })}</span>
        </span>
      }
      loading={loading && list.length === 0}
      loadingHeight={500}
      empty={isEmpty}
      emptyHeight={500}
      hasEmptyTitle={showEmptyTitle}
      hasEmptyDesc={showEmptyDesc}
      emptyDescElement={emptyDescElement}
      operations={[
        {
          element: (
            <SortBySelect
              borderNone={true}
              defaultValue={sortBy}
              onChange={(_key: string, value: string) => {
                handleSortChange(value as TSortBy);
              }}
              defaultParams={{
                options: SORT_OPTIONS,
                btnTextPrefix: formatMessage({ id: 'Sort By' }),
              }}
            />
          ),
        },
      ]}
      className="dbo-discussion-list"
    >
      <List
        onScrollBottom={handleScrollBottom}
        className="dbo-discussion-list-items"
      >
        {list.map((item) => (
          <List.Item key={item.cluster_id}>
            <DiscussionItem
              item={item}
              isSelected={selectItem?.cluster_id === item.cluster_id}
              onClick={handleItemClick}
              activeKey={activeKeysMap?.[sortBy]}
              trendData={trendMap?.[item.cluster_id]}
              trendLoading={trendLoading}
              timelineStartTime={timelineStartTime}
              timelineEndTime={timelineEndTime}
            />
          </List.Item>
        ))}
        {loadingMore && (
          <List.Item className="dbo-discussion-list-loading-more">
            <StatusTip status="loading" />
          </List.Item>
        )}
      </List>
    </CardCommon>
  );
}
